import React, { useEffect, useMemo, useState } from 'react';
import { Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { apiCall } from '../../services/apiUtil';
import { academyAPI } from '../../services/api';
import { SQUAD_ROLES, roleLabel } from '../utils/squadRoles';

/**
 * WHO A CLASS SQUAD ADDS: THE PROGRAM'S STUDENTS AND THE ACADEMY'S STAFF.
 *
 * A squad a program runs as a class does not search the whole platform or
 * invite strangers by email — the people it needs are already known. This lists
 * them, lets several be ticked at once, and invites them in a single request.
 *
 * Props:
 * - groupId, programId, academyId: the class squad and the program it belongs to
 * - existingUserIds: who is already in the squad (or invited), shown but not pickable
 * - onAdded: called after a successful add, to re-read the squad's members
 */
const TABS = [
  { id: 'program', label: 'Program', defaultRole: 'player', empty: 'Nobody is enrolled in this program yet.' },
  { id: 'staff', label: 'Academy staff', defaultRole: 'coach', empty: 'This academy has no staff yet.' },
];

const nameOf = (first, last) => `${first || ''} ${last || ''}`.trim();

const ClassMemberPicker = ({ groupId, programId, academyId, existingUserIds = [], onAdded }) => {
  const { theme } = useTheme();
  const [tab, setTab] = useState('program');
  const [lists, setLists] = useState({ program: null, staff: null });
  const [failed, setFailed] = useState({ program: false, staff: false });
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState('');
  const [role, setRole] = useState('player');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    academyAPI.enrollments.list(academyId, programId)
      .then((rows) => {
        if (!live) return;
        const program = (Array.isArray(rows) ? rows : [])
          .map(r => ({ userId: String(r.userId), name: nameOf(r.firstName, r.lastName) }));
        setLists(l => ({ ...l, program }));
      })
      .catch(() => { if (live) setFailed(f => ({ ...f, program: true })); });
    academyAPI.staff.list(academyId)
      .then((rows) => {
        if (!live) return;
        // Only people who have joined the staff: an invitation not yet taken up
        // has nobody behind it to add.
        const staff = (Array.isArray(rows) ? rows : [])
          .filter(s => s.userId && s.isActive)
          .map(s => ({ userId: String(s.userId), name: s.name }));
        setLists(l => ({ ...l, staff }));
      })
      .catch(() => { if (live) setFailed(f => ({ ...f, staff: true })); });
    return () => { live = false; };
  }, [academyId, programId]);

  const inSquad = useMemo(() => new Set(existingUserIds.map(String)), [existingUserIds]);
  const people = lists[tab];
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (people || []).filter(p => !q || p.name.toLowerCase().includes(q));
  }, [people, filter]);
  const pickable = shown.filter(p => !inSquad.has(p.userId));
  const allPicked = pickable.length > 0 && pickable.every(p => selected.has(p.userId));

  const switchTab = (id) => {
    setTab(id);
    setSelected(new Set());
    setFilter('');
    setRole(TABS.find(t => t.id === id).defaultRole);
  };

  const toggle = (userId) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    return next;
  });

  const toggleAll = () => setSelected(allPicked ? new Set() : new Set(pickable.map(p => p.userId)));

  const add = async () => {
    setSaving(true);
    try {
      const result = await apiCall(`/groups/${groupId}/members/bulk`, {
        method: 'POST',
        body: JSON.stringify({ members: [...selected].map(targetUserId => ({ targetUserId, role })) }),
      });
      toast.success(`Invited ${result.added} to the squad`);
      setSelected(new Set());
      if (onAdded) await onAdded();
    } catch (error) {
      toast.error(error.message || 'Could not add them. Please try again.');
    }
    setSaving(false);
  };

  const current = TABS.find(t => t.id === tab);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className={`p-4 border-b ${theme.border} flex gap-x-2`} role="tablist">
        {TABS.map(t => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => switchTab(t.id)}
            className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${
              tab === t.id ? 'bg-blue-500 text-white' : `${theme.backgroundSecondary} ${theme.text}`
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-4 pt-4 flex items-center gap-x-2">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name"
          aria-label="Filter by name"
          className={`flex-1 min-w-0 text-sm rounded-lg border ${theme.border} ${theme.input} px-3 py-2`}
        />
        <label htmlFor="class-join-as" className={`text-sm font-medium ${theme.text} whitespace-nowrap`}>Join as</label>
        <select
          id="class-join-as"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className={`text-sm rounded-lg border ${theme.border} ${theme.input} px-2 py-2`}
        >
          {SQUAD_ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
        </select>
      </div>

      <div className="p-4 flex-1 overflow-y-auto min-h-0">
        {failed[tab] ? (
          <p className={`text-sm ${theme.textMuted} py-6 text-center`}>Could not load this list.</p>
        ) : people === null ? (
          <div className="flex justify-center py-6"><Loader className="w-5 h-5 animate-spin" /></div>
        ) : shown.length === 0 ? (
          <p className={`text-sm ${theme.textMuted} py-6 text-center`}>{filter ? 'Nobody matches.' : current.empty}</p>
        ) : (
          <ul className="space-y-1">
            {shown.map((p) => {
              const already = inSquad.has(p.userId);
              return (
                <li key={p.userId}>
                  <label className={`flex items-center gap-x-3 px-2 py-2 rounded-lg ${already ? 'opacity-60' : `cursor-pointer ${theme.cardHover}`}`}>
                    <input
                      type="checkbox"
                      checked={already || selected.has(p.userId)}
                      disabled={already}
                      onChange={() => toggle(p.userId)}
                      aria-label={p.name}
                    />
                    <span className={`flex-1 text-sm ${theme.text}`}>{p.name}</span>
                    {already && <span className={`text-xs ${theme.textMuted}`}>In squad</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className={`p-4 border-t ${theme.border} flex items-center justify-between`}>
        <label className={`flex items-center gap-x-2 text-sm ${theme.text}`}>
          <input type="checkbox" checked={allPicked} disabled={pickable.length === 0} onChange={toggleAll} />
          Select all
        </label>
        <button
          onClick={add}
          disabled={selected.size === 0 || saving}
          className={`${theme.buttonPrimary} px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50`}
        >
          {saving ? 'Adding…' : `Add ${selected.size}`}
        </button>
      </div>
    </div>
  );
};

export default ClassMemberPicker;
